<template>
  <div class="ip-book-card">
    <div class="tt-header">
      <span>{{ title }}</span>
      <span v-if="tag" class="tt-tag" :class="'tt-tag--' + tagKind">{{ tag }}</span>
    </div>

    <div class="tt-sides">
      <div class="tt-side tt-side--long">
        <div class="tt-side__name">{{ isTargetMode ? 'Longs alvo' : 'Longs abertos' }}</div>
        <div class="tt-side__qty">{{ formatBtc(stats.longQty) }} BTC</div>
        <div class="tt-side__row">
          Avg
          <strong class="text-orange">{{ stats.longQty > 0 ? '$' + formatUsd(stats.avgLongPrice) : '—' }}</strong>
        </div>
        <div class="tt-side__row">
          PnL
          <strong :class="pnlClass(stats.spotPnl)">{{ stats.longQty > 0 ? formatPnl(stats.spotPnl) : '—' }}</strong>
        </div>
      </div>

      <div class="tt-side tt-side--short">
        <div class="tt-side__name">{{ isTargetMode ? 'Shorts alvo' : 'Shorts abertos' }}</div>
        <div class="tt-side__qty">{{ formatBtc(stats.shortQty) }} BTC</div>
        <div class="tt-side__row">
          Avg
          <strong class="text-purple">{{ stats.shortQty > 0 ? '$' + formatUsd(stats.avgShortPrice) : '—' }}</strong>
        </div>
        <div class="tt-side__row">
          PnL
          <strong :class="pnlClass(stats.shortPnl)">{{ stats.shortQty > 0 ? formatPnl(stats.shortPnl) : '—' }}</strong>
        </div>
      </div>
    </div>

    <div class="tt-foot">
      <div v-if="stats.hasAvgSpread" class="tt-foot__row">
        Δ Avg (S−L)
        <strong :class="pnlClass(stats.avgSpread)">
          {{ stats.avgSpread >= 0 ? '+' : '−' }}${{ formatUsd(Math.abs(stats.avgSpread)) }}
        </strong>
      </div>
      <div v-if="stats.overlappedQty > 0" class="tt-foot__row">
        Qty casada
        <strong>{{ formatBtc(stats.overlappedQty) }} BTC</strong>
      </div>
      <div class="tt-foot__row">
        Net Delta
        <strong :class="pnlClass(stats.netDelta)">
          {{ formatBtc(stats.netDelta) }} BTC<span v-if="stats.residualSide !== 'flat'"> ({{ stats.residualSide === 'long' ? 'Long' : 'Short' }})</span>
        </strong>
      </div>
      <div class="tt-foot__row">
        PnL líquido
        <strong :class="pnlClass(stats.netPnl)">{{ formatPnl(stats.netPnl) }}</strong>
      </div>
    </div>
  </div>
</template>

<script setup>
// eslint-disable-next-line no-undef -- compiler macro (script setup)
defineProps({
  stats: { type: Object, required: true },
  title: { type: String, default: '' },
  tag: { type: String, default: '' },
  tagKind: { type: String, default: 'bin' },
  isTargetMode: { type: Boolean, default: false },
})

function formatBtc(value) {
  return Number(value || 0).toFixed(4)
}

function formatUsd(value) {
  return Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatPnl(value) {
  const n = Number(value || 0)
  const sign = n > 0 ? '+' : n < 0 ? '−' : ''
  return `${sign}$${Math.abs(n).toFixed(2)}`
}

function pnlClass(value) {
  return Number(value || 0) >= 0 ? 'text-green' : 'text-red'
}
</script>

<style scoped>
.ip-book-card {
  min-width: 280px;
  color: #c9d1d9;
}

.tt-header {
  font-weight: 700;
  color: #58a6ff;
  margin-bottom: 8px;
  border-bottom: 1px solid #30363d;
  padding-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  white-space: nowrap;
}

.tt-tag {
  font-size: 9px;
  padding: 1px 4px;
  border-radius: 3px;
  font-weight: 700;
}

.tt-tag--bin {
  background: rgba(88, 166, 255, 0.15);
  color: #58a6ff;
}

.tt-tag--target {
  background: rgba(227, 179, 65, 0.2);
  color: #e3b341;
}

.tt-tag--range {
  background: rgba(88, 166, 255, 0.2);
  color: #58a6ff;
  border: 1px solid #388bfd;
}

.tt-sides {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  margin-bottom: 8px;
}

.tt-side {
  padding: 4px 8px 2px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.tt-side--long {
  border-right: 1px solid #30363d;
  padding-left: 0;
  text-align: right;
  align-items: flex-end;
}

.tt-side--short {
  padding-right: 0;
}

.tt-side--long .tt-side__row {
  flex-direction: row-reverse;
}

.tt-side__name {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tt-side--long .tt-side__name { color: #3fb950; }
.tt-side--short .tt-side__name { color: #f85149; }

.tt-side__qty {
  font-size: 13px;
  font-weight: 700;
  color: #f0f6fc;
}

.tt-side__row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: #8b949e;
}

.tt-foot {
  border-top: 1px solid #30363d;
  padding-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.tt-foot__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 11px;
  color: #c9d1d9;
}

.text-orange { color: #f39c12; }
.text-purple { color: #9b59b6; }
.text-green  { color: #3fb950; }
.text-red    { color: #f85149; }
</style>
